import { History } from "lucide-react";
import { useMemo, type FC } from "react";
import { Link } from "react-router-dom";

import type {
  OutgoingIntake,
  OutgoingIntakeSubmission
} from "@/api/types/outgoingIntake";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { ActivityList } from "../components/ActivityList";
import { intakeActivity } from "../intakeActivity";
import { clockLabel } from "../model";

type Props = {
  intake: OutgoingIntake;
  submissions: OutgoingIntakeSubmission[];
  clientId: string;
  clientLabel?: string;
  className?: string;
};

/**
 * The intake's history behind a toolbar trigger: sent, delivered, opened,
 * reminded, submitted, changes asked for, accepted and filed, with any
 * automatic reminders still to come, and a link to the client's Activity.
 */
export const TimelinePopover: FC<Props> = ({
  intake,
  submissions,
  clientId,
  clientLabel = "Client",
  className
}) => {
  const items = useMemo(
    () => intakeActivity(intake, submissions),
    [intake, submissions]
  );
  const first = items[0]?.at ?? null;
  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Timeline"
              className={cn("text-fg-3 hover:text-fg", className)}
            >
              <History />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">Timeline</TooltipContent>
      </Tooltip>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between border-b border-line-1 px-3 py-2">
          <span className="text-base font-medium text-fg">Timeline</span>
          {first ? (
            <span className="text-sm text-fg-3">since {clockLabel(first)}</span>
          ) : null}
        </div>
        <ActivityList
          items={items}
          clientLabel={clientLabel}
          className="max-h-80 overflow-y-auto px-3 py-2"
        />
        <div className="border-t border-border px-3 py-2">
          <Link
            to={`/clients/${clientId}?tab=activity`}
            className="text-xs font-medium text-primary hover:underline"
          >
            Open in Activity
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
};
